import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import VisitService from "../../services/VisitService";

const ACCENT = "#8e44ad";

function AestheticMedicineVisits() {
  const { patientID } = useParams();
  const navigate = useNavigate();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadVisits = async () => {
      try {
        const data = await VisitService.getAllVisitsByPatientId(patientID);

        if (!Array.isArray(data)) {
          setVisits([]);
          return;
        }

        const filteredVisits = data
          .map(visit => {
            const items = (visit.visitItemDTOS || [])
              .filter(item => item.treatmentTypeDTO?.typeName === "AestheticMedicine");
            return items.length > 0 ? { ...visit, visitItemDTOS: items } : null;
          })
          .filter(Boolean)
          .sort((a, b) => new Date(b.visitDate) - new Date(a.visitDate));

        setVisits(filteredVisits);
      } catch (error) {
        console.error("Greška:", error);
        setVisits([]);
      } finally {
        setLoading(false);
      }
    };
    loadVisits();
  }, [patientID]);

  const handleDeleteVisit = async (visitID) => {
    if (!window.confirm("Da li ste sigurni da želite da obrišete ovaj tretman?")) return;
    try {
      await VisitService.deleteVisit(visitID);
      setVisits(prev => prev.filter(v => v.visitID !== visitID));
    } catch (err) {
      alert("Greška pri brisanju: " + err.message);
    }
  };

  if (loading) return <div className="container mt-4 text-center">Učitavanje poseta...</div>;

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString("sr-RS");

  const patient = visits[0]?.patientDTO;

  return (
    <div style={{ backgroundColor: "#f8f7fb", minHeight: "100vh", paddingBottom: "40px" }}>
      {/* HEADER BANER */}
      <div style={{
        background: `linear-gradient(135deg, ${ACCENT} 0%, #6c3483 100%)`,
        color: "#fff",
        padding: "28px 20px",
        marginBottom: "25px",
        boxShadow: "0 3px 10px rgba(0,0,0,0.15)",
      }}>
        <div className="container" style={{ maxWidth: "850px" }}>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h3 className="mb-1" style={{ fontWeight: 700 }}>💆 Estetska medicina</h3>
              {patient && (
                <div style={{ opacity: 0.9 }}>{patient.name} {patient.surname}</div>
              )}
            </div>
            <button className="btn btn-light btn-sm fw-bold" onClick={() => navigate(-1)}>← Nazad</button>
          </div>
        </div>
      </div>

      <div className="container" style={{ maxWidth: "850px" }}>
        {/* PODACI O PACIJENTU */}
        {patient && (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <div className="d-flex flex-wrap gap-4 mb-3 text-muted">
                <span>📞 {patient.phone || "/"}</span>
                <span>🎂 {patient.birthDate ? formatDate(patient.birthDate) : "/"}</span>
                {patient.email && <span>✉️ {patient.email}</span>}
              </div>
              <div className="row g-2">
                <div className="col-md-4">
                  <div style={{ backgroundColor: "#fff5f5", border: "1px solid #feb2b2", borderRadius: "8px", padding: "10px 12px", height: "100%" }}>
                    <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#c0392b" }}>ALERGIJE ⚠️</div>
                    <div style={{ wordBreak: "break-word" }}>{patient.allergies || "Nema"}</div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div style={{ backgroundColor: "#fdf8e3", border: "1px solid #faebcc", borderRadius: "8px", padding: "10px 12px", height: "100%" }}>
                    <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#8a6d3b" }}>OPERACIJE</div>
                    <div style={{ wordBreak: "break-word" }}>{patient.surgeries || "/"}</div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div style={{ backgroundColor: "#f0fff4", border: "1px solid #c6f6d5", borderRadius: "8px", padding: "10px 12px", height: "100%" }}>
                    <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#2f855a" }}>HRONIČNO</div>
                    <div style={{ wordBreak: "break-word" }}>{patient.otherInfo || "/"}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ISTORIJA PROCEDURA */}
        {visits.length === 0 ? (
          <div className="text-center text-muted py-5">
            <div style={{ fontSize: "2.5rem" }}>💆</div>
            <p className="fst-italic mt-2">Nema evidentiranih tretmana estetske medicine.</p>
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {visits.map((visit) =>
              visit.visitItemDTOS.map((item, iIndex) => (
                <div
                  className="card border-0 shadow-sm"
                  key={`${visit.visitID}-${iIndex}`}
                  style={{ borderLeft: `5px solid ${ACCENT}`, transition: "transform 0.15s, box-shadow 0.15s" }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.12)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = ""; }}
                >
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="badge" style={{ backgroundColor: ACCENT, color: "#fff", fontSize: "0.8rem" }}>
                        📅 {formatDate(visit.visitDate)}
                      </span>
                      <button
                        className="btn btn-sm"
                        style={{ color: "#e53e3e", border: "none", background: "none", fontSize: "1rem", lineHeight: 1 }}
                        onClick={() => handleDeleteVisit(visit.visitID)}
                        title="Obriši tretman"
                      >
                        🗑️
                      </button>
                    </div>
                    <div className="mb-2">
                      <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#999", letterSpacing: "0.03em" }}>OPIS PROCEDURE</div>
                      <div style={{ whiteSpace: "pre-wrap" }}>{item.treatmentTypeDTO?.procedureDescription || "/"}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#999", letterSpacing: "0.03em" }}>OPŠTA NAPOMENA</div>
                      <div style={{ whiteSpace: "pre-wrap" }}>
                        {item.notes && item.notes.trim() !== "" ? item.notes : (visit.notes || "/")}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AestheticMedicineVisits;
